import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Pencil, X, Loader2, Megaphone } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const LANGS = ["en", "hu", "de", "fr", "cs"];
const EMPTY = { title: {}, body: {}, active: true };
const fmt = (iso) => new Date(iso).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false });

export function ModNotices() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("en");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/notices").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setL = (k, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [tab]: v } }));
  const reset = () => { setForm(EMPTY); setEditId(null); setTab("en"); };

  const save = async () => {
    setSaving(true);
    try {
      const body = { title: form.title, body: form.body, active: form.active };
      if (editId) await api.put(`/admin/notices/${editId}`, body);
      else await api.post("/admin/notices", body);
      toast.success(editId ? "Közlemény frissítve — mindenkinek újra megjelenik" : "Közlemény létrehozva");
      reset();
      load();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (n, active) => { await api.patch(`/admin/notices/${n.id}/active`, { active }); load(); };
  const remove = async (n) => { await api.delete(`/admin/notices/${n.id}`); if (editId === n.id) reset(); load(); };

  return (
    <div className="mx-auto max-w-4xl" data-testid="mod-notices">
      <h2 className="text-lg font-semibold tracking-tight text-zinc-50">Kötelező közlemények</h2>
      <p className="mb-4 text-[12px] text-zinc-500">Az aktív közlemény teljes képernyős ablakban jelenik meg a nyilvános oldalak minden látogatójának, és csak elfogadás után lehet továbblépni. Egyszerre egy lehet aktív; módosításkor mindenkinek újra megjelenik.</p>

      <div className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
        <div className="mb-3 flex items-center gap-2 text-[13px] font-medium text-zinc-200">
          {editId ? <Pencil size={15} className="text-blue-400" /> : <Plus size={15} className="text-blue-400" />}
          {editId ? "Közlemény szerkesztése" : "Új közlemény"}
          {editId && <button data-testid="notice-cancel-edit" onClick={reset} className="ml-auto inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300"><X size={13} /> Mégse</button>}
        </div>
        <div className="mb-3 flex gap-1">
          {LANGS.map((l) => (
            <button key={l} data-testid={`notice-lang-${l}`} onClick={() => setTab(l)}
              className={`rounded-md px-2.5 py-1 text-[12px] font-semibold uppercase ${tab === l ? "bg-blue-600 text-white" : "border border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
              {l}{l === "en" && " *"}{(form.title[l] || form.body[l]) && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 align-middle" />}
            </button>
          ))}
        </div>
        <Input data-testid="notice-title" value={form.title[tab] || ""} onChange={(e) => setL("title", e.target.value)} placeholder={`Cím (${tab.toUpperCase()})`} className="h-9 border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
        <Textarea data-testid="notice-body" value={form.body[tab] || ""} onChange={(e) => setL("body", e.target.value)} placeholder={`Szöveg (${tab.toUpperCase()}) — üresen hagyva az angol jelenik meg`} className="mt-2 min-h-[160px] border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-[13px] text-zinc-300">
            <Switch data-testid="notice-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Aktív
          </label>
          <button data-testid="notice-save" onClick={save} disabled={saving}
            className="ml-auto inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {editId ? "Mentés" : "Létrehozás"}
          </button>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {items.length === 0 && <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-6 text-center text-[13px] text-zinc-600">Még nincs közlemény.</div>}
        {items.map((n) => (
          <div key={n.id} data-testid={`notice-item-${n.id}`} className={`flex items-start gap-3 rounded-lg border p-3.5 ${n.active ? "border-blue-500/40 bg-blue-600/[0.06]" : "border-zinc-800/70 bg-[#111113]"}`}>
            <Megaphone size={16} className={n.active ? "mt-0.5 text-blue-400" : "mt-0.5 text-zinc-600"} />
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium text-zinc-100">{n.title?.hu || n.title?.en}</div>
              <div className="text-[11px] text-zinc-500">Frissítve: {fmt(n.updated_at)} · <span data-testid={`notice-accepts-${n.id}`}>{n.accept_count} elfogadás</span></div>
            </div>
            <Switch data-testid={`notice-toggle-${n.id}`} checked={n.active} onCheckedChange={(v) => toggle(n, v)} />
            <button data-testid={`notice-edit-${n.id}`} onClick={() => { setEditId(n.id); setForm({ title: n.title || {}, body: n.body || {}, active: n.active }); }} className="grid h-8 w-8 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil size={14} /></button>
            <button data-testid={`notice-delete-${n.id}`} onClick={() => remove(n)} className="grid h-8 w-8 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40 hover:text-red-300"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
